import type { RouteArgs } from '@ember/routing/lib/utils';
import type PublicTransition from '@ember/routing/transition';
import { later, once, schedule } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { APIData, ImageCropData, PopupData, Service } from 'webapp-types';
import { AiChatRouteSource } from 'webapp/enums/ai-chat';
import type { Theme as ThemeEnum } from 'webapp/enums/enums';
import { AppType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { redirect, reload } from 'webapp/utils/common';

import AccountPopupInactivityComponent from 'webapp/components/account/popup-inactivity';
import BillingPopupRequestUpgradeComponent from 'webapp/components/billing/popup-request-upgrade';
import BillingPopupTrialActivatedComponent from 'webapp/components/billing/popup-trial-activated';
import CommonImageUploadPopupComponent from 'webapp/components/common/image-upload/popup';
import CommonPopupContextSwitchComponent from 'webapp/components/common/popup-context-switch';
import CommonPopupsPopupBlueBubble from 'webapp/components/common/popups/popup-blue-bubble';
import CommonPopupsDebuggingComponent from 'webapp/components/common/popups/popup-debugging';
import CommonPopupsReloadModalComponent from 'webapp/components/common/popups/reload-modal';
import CommonPopupsVideoComponent from 'webapp/components/common/popups/video';
import CustomMetricsEditPopupComponent from 'webapp/components/custom-metrics/edit/popup';
import DesignerTopbarPopupDataboardsComponent from 'webapp/components/designer/topbar/popup-databoards';
import MobilePopupAppSuggestionComponent from 'webapp/components/mobile/popup-app-suggestion';
import NavigationPopupFullscreenLoaderComponent from 'webapp/components/navigation/popup-fullscreen-loader';
import NavigationPopupLogoutComponent from 'webapp/components/navigation/popup-logout';
import type ApplicationRoute from 'webapp/routes/application';

import type PopupModal from 'webapp/models-custom/popup/modal';
import Theme from 'webapp/models-custom/theme';
import type BoardRecord from 'webapp/models/board';
import DataLoaderDatawall from 'webapp/data-loader/datawall';
import DataLoaderE2ESeed from 'webapp/data-loader/e2e-seed';
import DataLoaderMain from 'webapp/data-loader/main';
import DataLoaderMobile from 'webapp/data-loader/mobile';
import DataLoaderSharedArtifact from 'webapp/data-loader/shared-artifact';
import DataLoaderSharedChat from 'webapp/data-loader/shared-chat';

import { Severity } from 'webapp/services/logger';

// logged on a forced logout in the mobile app
export interface LogoutLogData {
	t?: string; // the Authorization header
	requestId?: string;
}

type DataLoader =
	| DataLoaderMain
	| DataLoaderDatawall
	| DataLoaderMobile
	| DataLoaderSharedChat
	| DataLoaderSharedArtifact
	| DataLoaderE2ESeed;

// The Genie page and a routine run are a chat themselves, so the Genie side panel stays off on them
function isGenieChatRoute(routeName: string): boolean {
	return (
		routeName.startsWith(AiChatRouteSource.GENIE) ||
		routeName.startsWith(AiChatRouteSource.ROUTINE_RUN)
	);
}

export default class NavigationService extends EmberService {
	@service declare router: Service.Router;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('intercom') declare intercomService: Service.Intercom;
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('tour') declare tourService: Service.Tour;
	@service('logger') declare loggerService: Service.Logger;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;
	@service('ai-chat') declare aiChatService: Service.AIChat;

	@tracked declare appType: AppType;
	@tracked activatedRoute = '';
	@tracked activatedRouteParams: Record<string, unknown> = {};

	@tracked theme!: Theme;

	@tracked isFontLoaded = false;

	@tracked addButtonContainerNode: HTMLElement | null = null;

	@tracked batchButtonContainerNode: HTMLElement | null = null;

	@tracked generateWithAIButtonContainerNode: HTMLElement | null = null;

	@tracked isFilterSearchActive = false;

	appRoute!: ApplicationRoute;
	dataLoader!: DataLoader;

	bindRouterEvents() {
		this.router.on('routeWillChange', (transition: PublicTransition) => {
			// Held back by the leave guard, which runs first (it listens on the EmberRouter): nothing
			// navigates, so nothing closes.
			if (transition.isAborted) {
				return;
			}

			document.body.classList.add('navigating');
			this.closeAllPopups1(transition);
		});

		this.router.on('routeDidChange', (transition: PublicTransition) => {
			document.body.classList.remove('navigating');
			this.closeAllTooltips();
			this.triggerTransitionOnWindow();
			this.updateGA();
			this.intercomService.onRouteChange();

			// only update analytics if the route name actually changed
			const fromName = transition.from?.name || '';
			const toName = transition.to?.name || '';
			if (fromName !== toName) {
				this.updateAnalytics();
			}

			this.updateInspectlet();
			// this.updateHotjar();
			this.updateSegment();
			this.resetScroll(transition);
			this.automatedPopupQueueService.checkToOpenPopup();
		});
	}

	transitionToRoute(name: string, ...args: RouteArgs): PublicTransition {
		return this.router.transitionTo(name, ...args);
	}

	replaceRoute(name: string, ...args: RouteArgs): PublicTransition {
		return this.router.replaceWith(name, ...args);
	}

	get activeCustomizeTab(): string | null {
		return (this.router.currentRoute?.queryParams?.customize as string) ?? null;
	}

	updateQueryParams(queryParams: Record<string, unknown>): PublicTransition {
		return this.router.transitionTo({ queryParams });
	}

	get isMainApp(): boolean {
		return this.appType === AppType.MAIN;
	}

	get isDatawallApp(): boolean {
		return this.appType === AppType.DATAWALL;
	}

	get isE2EApp(): boolean {
		return this.appType === AppType.E2E;
	}

	// The Company metrics page, also under its popup routes.
	get isActivatedRouteCompanyMetrics(): boolean {
		return this.activatedRoute.startsWith('metrics');
	}

	get isActivatedRouteGoals(): boolean {
		return this.activatedRoute.startsWith('goals');
	}

	get isActivatedRouteGenieShared(): boolean {
		return this.activatedRoute === AiChatRouteSource.SHARED_CHAT;
	}

	get isActivatedRouteSharedArtifact(): boolean {
		return this.activatedRoute === AiChatRouteSource.SHARED_ARTIFACT;
	}

	get isSharedChatApp(): boolean {
		return this.appType === AppType.SHARED_CHAT;
	}

	get isSharedArtifactApp(): boolean {
		return this.appType === AppType.SHARED_ARTIFACT;
	}

	get isMobileApp(): boolean {
		return this.appType === AppType.MOBILE;
	}

	get isLoadedFromSnapshooter(): boolean {
		return typeof window.callSnapshooter === 'function';
	}

	get isDatawallAndExpired(): boolean {
		const dataLoader = this.dataLoader;
		return dataLoader instanceof DataLoaderDatawall && dataLoader.isDatawallExpired;
	}

	get isDatawallAndInactive(): boolean {
		const dataLoader = this.dataLoader;
		return dataLoader instanceof DataLoaderDatawall && dataLoader.isDatawallInactive;
	}

	get isDatawallAndPasswordProtected(): boolean {
		const dataLoader = this.dataLoader;
		return dataLoader instanceof DataLoaderDatawall && dataLoader.isDatawallPasswordProtected;
	}

	get isDatawallAndShowLock(): boolean {
		const dataLoader = this.dataLoader;
		return dataLoader instanceof DataLoaderDatawall && dataLoader.showDatawallLock;
	}

	get spaceIdOfDatawall(): number | null {
		const dataLoader = this.dataLoader;
		return dataLoader instanceof DataLoaderDatawall ? dataLoader.spaceId : null;
	}

	set spaceIdOfDatawall(value: number | null) {
		const dataLoader = this.dataLoader;
		if (dataLoader instanceof DataLoaderDatawall) {
			dataLoader.spaceId = value;
		}
	}

	get isDatawallAndHasAccessToBoard(): boolean {
		const dataLoader = this.dataLoader;
		return dataLoader instanceof DataLoaderDatawall && dataLoader.hasAccessToBoard;
	}

	set isDatawallAndHasAccessToBoard(value: boolean) {
		const dataLoader = this.dataLoader;
		if (dataLoader instanceof DataLoaderDatawall) {
			dataLoader.hasAccessToBoard = value;
		}
	}

	get activatedSection(): string | null {
		const activatedRoute = String(this.activatedRoute);

		let section = null;

		if (activatedRoute.startsWith('genie')) {
			section = 'genie';
		} else if (activatedRoute.startsWith('index')) {
			section = 'home';
		} else if (activatedRoute.startsWith('boards')) {
			section = 'boards';
		} else if (activatedRoute.startsWith('goals')) {
			section = 'goals';
		} else if (activatedRoute.startsWith('reports')) {
			section = 'reports';
		} else if (
			activatedRoute.startsWith('datamanager') ||
			// Listed in the sidebar's Data panel.
			activatedRoute.startsWith('metric-library')
		) {
			section = 'datamanager';
		} else if (activatedRoute.startsWith('notifications')) {
			section = 'notifications';
		} else if (activatedRoute.startsWith('settings')) {
			section = 'settings';
		} else if (activatedRoute.startsWith('clients')) {
			section = 'clients';
		} else if (activatedRoute.startsWith('metrics')) {
			section = 'metrics';
		} else if (activatedRoute.startsWith('wizard')) {
			if (activatedRoute.includes('templates')) {
				section = 'wizard-templates';
			} else if (activatedRoute.includes('metrics')) {
				section = 'wizard-metrics';
			} else if (activatedRoute.includes('source')) {
				section = 'wizard-source';
			}
		} else if (activatedRoute.startsWith('mobile')) {
			section = 'mobile';
		} else if (activatedRoute.startsWith('storybook')) {
			section = 'storybook';
		} else if (activatedRoute.startsWith('teams')) {
			section = 'teams';
		}

		return section;
	}

	get isHome(): boolean {
		return this.activatedSection === 'home';
	}

	get sectionTitle(): string {
		switch (this.activatedSection) {
			case 'genie':
				return 'AI analyst';
			case 'metrics':
				return 'Metrics';
			case 'boards':
				return 'Dashboards';
			case 'goals':
				return 'Goals & OKRs';
			case 'reports':
				return 'Reports';
			case 'datamanager':
				return 'Data manager';
			case 'notifications':
				return 'Notifications';
			case 'settings':
				return 'Settings';
			case 'clients':
				return 'Accounts';
			case 'wizard-templates':
				return 'Pick a template';
			case 'wizard-metrics':
				return 'Select metrics to track';
			case 'wizard-source':
				return `Choose ${this.currentUserService.isOnboarded ? 'a' : 'your first'} data source`;
			case 'storybook':
				return 'Storybook';
			case 'teams':
				return 'Spaces';
			default:
				return '';
		}
	}

	get sectionIsNew() {
		const newSections: string[] = [];

		return newSections.includes(this.activatedSection || '');
	}

	get isReportEditorSubpage(): boolean {
		return this.activatedRoute.startsWith('reports.editor');
	}

	get isDesigner(): boolean {
		return this.activatedRoute.startsWith('designer');
	}

	get isDatasets(): boolean {
		return this.activatedRoute.startsWith('datamanager.connected.datasets');
	}

	get isOnboarding(): boolean {
		return this.activatedRoute.startsWith('onboarding');
	}

	get isGenie(): boolean {
		return this.activatedRoute.startsWith('genie');
	}

	get isGenieChatPage(): boolean {
		return isGenieChatRoute(this.activatedRoute);
	}

	get isArtifacts(): boolean {
		return this.activatedRoute.startsWith('artifacts');
	}

	get isRoutines(): boolean {
		return this.activatedRoute.startsWith('routines');
	}

	get isWizard(): boolean {
		return this.activatedRoute.startsWith('wizard');
	}

	get isComingSoonPages(): boolean {
		return this.activatedRoute.startsWith('agents');
	}

	get initialRequestsPromise() {
		return this.dataLoader.initialRequestsPromise;
	}

	get initialRequestsDone() {
		return this.dataLoader.initialRequestsDone;
	}

	getPathFromRouteName(routeName: string, ...args: (string | null)[]): string {
		return this.router.urlFor(routeName, ...args);
	}

	getAbsoluteUrlForRoute(routeName: string, ...args: (string | null)[]): string {
		return `${window.location.origin}${this.getPathFromRouteName(routeName, ...args)}`;
	}

	closeAllPopups1(transition: PublicTransition): void {
		let closePopups = true;

		const fromName = transition.from?.name || '',
			toName = transition.to?.name || '';

		// dont close popups when navigating board templates (routed modals)
		const boardTemplatesPaths = [
			'boards.public-templates.templates.template',
			'boards.public-templates.template.index',
			'boards.public-templates.template.connect',
		];
		if (boardTemplatesPaths.includes(fromName) && boardTemplatesPaths.includes(toName)) {
			closePopups = false;
		}
		if (
			(toName.startsWith('boards.index') || // also can be boards.index_loading
				toName.startsWith('boards.loops')) &&
			(boardTemplatesPaths.includes(fromName) || fromName === 'boards.account-templates')
		) {
			// exception when creating a template
			closePopups = false;
		}

		// dont close popups when navigating report templates (routed modals)
		const reportTemplatesPaths = [
			'reports.public-templates.templates.template',
			'reports.public-templates.template.index',
			'reports.public-templates.template.connect',
		];
		if (reportTemplatesPaths.includes(fromName) && reportTemplatesPaths.includes(toName)) {
			closePopups = false;
		}
		if (
			toName.startsWith('reports.index') && // also can be reports.index_loading
			(reportTemplatesPaths.includes(fromName) || fromName === 'reports.account-templates')
		) {
			// exception when creating a template
			closePopups = false;
		}

		// dont close popups when navigating custom metric edit (routed modals over the Metrics page)
		// need this to correctly handle duplicate (opening new one before old closes)
		const isCustomMetricEditOrRoot = (name: string) =>
			name === 'metrics.index' || name.startsWith('metrics.edit');
		if (isCustomMetricEditOrRoot(fromName) && isCustomMetricEditOrRoot(toName)) {
			closePopups = false;
		}

		// don't close databoard-menu if still in designer
		try {
			if (
				this.popupService.isOpen(DesignerTopbarPopupDataboardsComponent) &&
				toName.startsWith('designer')
			) {
				closePopups = false;
			}
		} catch (_e) {
			// skip
		}

		// don't close inactivity popup
		try {
			if (this.popupService.isOpen(AccountPopupInactivityComponent)) {
				closePopups = false;
			}
		} catch (_e) {
			// skip
		}

		// don't close trial activated popup in wizard
		try {
			if (
				this.popupService.isOpen(BillingPopupTrialActivatedComponent) &&
				toName.startsWith('wizard')
			) {
				closePopups = false;
			}
		} catch (_e) {
			// skip
		}

		// don't close onboarding bubbles while switching between blocks on designer
		try {
			if (
				this.popupService.isOpen(CommonPopupsPopupBlueBubble) &&
				fromName.startsWith('designer') &&
				toName.startsWith('designer')
			) {
				closePopups = false;
			}
		} catch (_e) {
			// skip
		}

		// don't close request upgrade popup
		try {
			if (
				this.popupService.isOpen(BillingPopupRequestUpgradeComponent) &&
				toName.startsWith('datamanager')
			) {
				closePopups = false;
			}
		} catch (_e) {
			// skip
		}

		// The side panel stays open across routes; only a full-screen Genie chat replaces it
		if (this.aiChatService.isOpen && isGenieChatRoute(toName)) {
			this.aiChatService.closeByRouteChange();
		}

		if (closePopups) {
			try {
				void this.popupService.closeAll();
			} catch (_e) {
				// skip - can throw an error on app's first transition
			}
		}
	}

	closeAllTooltips(): void {
		$('body >.tooltipster-base').remove();
	}

	triggerTransitionOnWindow(): void {
		schedule('afterRender', () => {
			$(window).trigger('appDidTransition');
		});
	}

	updateGA(): void {
		if (window.ga) {
			window.ga('send', 'pageview', window.location.pathname);
		}
	}

	updateInspectlet(): void {
		if (window.__insp) {
			window.__insp.push(['virtualPage']);
		}
	}

	// updateHotjar(): void {
	// 	if (window.hj) {
	// 		window.hj('vpv', window.location.pathname);
	// 	}
	// }

	updateAnalytics(): void {
		this.analyticsService.pageFromPath(this.router.currentRouteName || '');
	}

	updateSegment(): void {
		if (window.analytics) {
			try {
				window.analytics.page();
			} catch (_e) {
				// skip
			}
		}
	}

	resetScroll(transition: PublicTransition): void {
		let preventScrollReset = false;

		const fromName = transition.from?.name || '',
			toName = transition.to?.name || '';

		const routes = [
			'datamanager.available.index',
			'datamanager.available.source-type-info',
			// opening and closing public template preview (together with route below)
			'boards.public-templates.index',
			'boards.public-templates.template.index',
			'boards.public-templates.template.connect',
			// custom metric edit popup
			'metrics.index',
			'metrics.edit.built',
			'metrics.edit.calculated',
			'metrics.edit.horizon',
			// notifications edit popup
			'settings.notifications.index',
			'settings.notifications.edit',
		];

		if (routes.includes(fromName) && routes.includes(toName)) {
			preventScrollReset = true;
		}

		// opening, closing or switching tabs in the Genie settings popup keeps the page behind it in place
		if (
			fromName === toName &&
			(transition.from?.queryParams?.customize !== transition.to?.queryParams?.customize ||
				transition.from?.queryParams?.anchor !== transition.to?.queryParams?.anchor)
		) {
			preventScrollReset = true;
		}

		if (!preventScrollReset) {
			window.scrollTo(0, 0);
		}
	}

	logout(reason?: string, data?: LogoutLogData): void {
		if (this.isMobileApp) {
			this.loggerService.logError(
				'Mobile in-app logout',
				{ reason, t: data?.t, requestId: data?.requestId },
				Severity.INFO,
			);
			later(() => {
				redirect('/m/logout');
			}, 200);
		} else if (this.isMainApp) {
			redirect('/logout');
		} else {
			// public datawall - is ok to keep open, just cleanup user data
			this.currentUserService.accessToken = '';
			this.currentUserService.accessTokenExpiry = null;
			this.currentUserService.userDataToken = '';
			this.currentUserService.model = null;
		}
	}

	openReloadModal(props: PopupData.Common.ReloadModal = {}) {
		const popup = this.popupService.createModal(CommonPopupsReloadModalComponent, {
			popupClass: 'popup-type-reload-modal',
		});
		popup.open(props);
	}

	pusherLongtermInactivity(): void {
		let hardReload = true;

		// triggered when pusher has been offline for longer period of time
		// will refresh designer, or ask user to refresh in case he's on some form
		if (this.popupService.isOpen(CustomMetricsEditPopupComponent)) {
			hardReload = false;
		}

		// this.logger.logError('Pusher longterm inactivity', {popupOnly: !hardReload}, Severity.INFO);

		if (hardReload) {
			$('body').addClass('reloading');
			later(() => {
				reload();
			}, 200); // so event gets logged
		} else {
			this.openReloadModal();
		}
	}

	goToBoardsAndOpenPreview(
		boardRecord: BoardRecord,
		afterBoardsDataLoaded?: () => void,
		afterPopupClose?: () => void,
	): PublicTransition {
		boardRecord.newBadge = true;

		const transition = this.transitionToRoute('boards');

		transition.then(() => {
			schedule('afterRender', () => {
				// scroll to last created board
				document.body.scrollIntoView(false);

				const boardModel = this.mmBoardsService.createModelFromRecord(boardRecord),
					onBoardsDataLoaded = () => {
						if (afterBoardsDataLoaded) {
							afterBoardsDataLoaded();
						}
					},
					onPopupClose = () => {
						if (afterPopupClose) {
							afterPopupClose();
						}
					};

				this.mmBoardsService.openPreview(
					boardModel,
					null,
					[],
					onPopupClose,
					onBoardsDataLoaded,
				);
			});
		});

		return transition;
	}

	initializeDataLoader(): DataLoader {
		let dataLoader;
		if (this.isMainApp) {
			dataLoader = new DataLoaderMain();
		} else if (this.isDatawallApp) {
			dataLoader = new DataLoaderDatawall();
		} else if (this.isSharedChatApp) {
			dataLoader = new DataLoaderSharedChat();
		} else if (this.isSharedArtifactApp) {
			dataLoader = new DataLoaderSharedArtifact();
		} else if (this.isMobileApp) {
			dataLoader = new DataLoaderMobile();
		} else if (this.isE2EApp) {
			dataLoader = new DataLoaderE2ESeed();
		}
		this.dataLoader = dataLoader!;
		return this.dataLoader;
	}

	createTheme(themeId: ThemeEnum): void {
		this.theme = new Theme(themeId);
	}

	validateAppTransition(transition: PublicTransition): boolean {
		// a query-param-only transition stays on the current (already validated) route; it has no
		// targetName, so the redirects below would send it to the same route again, endlessly
		if (!this.isMainApp || transition.queryParamsOnly) {
			return true;
		}

		const targetName = transition.targetName || '',
			rootRoute = targetName.substring(0, targetName.indexOf('.')),
			isViewer = this.currentUserService.isViewer,
			defaultRedirectPath = isViewer ? 'boards.index' : 'index';

		let redirectPath;

		// prevent transition to any other route except /databoards and /databoards/tabs or databoard in-app view for viewer user
		if (
			this.currentUserService.model?.isViewer &&
			!['boards.index', 'boards.tabs', 'mobile.board'].includes(targetName) &&
			!targetName.startsWith('boards.view.board')
		) {
			this.replaceRoute('boards.index');
			return false;
		}

		if (!this.initialRequestsDone) {
			void this.initialRequestsPromise.then(() => {
				once(this, this.validateAppTransition, transition);
			});
			return false;
		}

		if (this.billingService.showPaymentErrorWarning) {
			if (this.currentUserService.isAdmin) {
				if (!targetName.startsWith('settings.billing')) {
					this.billingService.openPaymentErrorPopup();
					// change transition only when first enter route and abort without transition for every
					// other attempts (bc of active route)
					if (transition.sequence < 1) {
						// sometimes is -1
						redirectPath = 'settings.billing';
					} else {
						transition.abort();
					}
				}
			} else {
				this.billingService.openPaymentErrorPopup();
				redirectPath = defaultRedirectPath;
			}
		} else if (
			this.billingService.agencyClientPackDowngradeLimitsBreached &&
			!isViewer &&
			!['home', 'index', 'settings', 'clients', 'datamanager'].some((r) =>
				targetName.startsWith(r),
			)
		) {
			this.replaceRoute('index')
				.followRedirects()
				.then(() => {
					this.billingService.openAgencyBlockerPopup();
				});
			return false;
		} else {
			const isRouteBilling = targetName === 'settings.billing.index',
				isRoutePlans = targetName === 'settings.billing.plans',
				shouldKickOut = isRouteBilling
					? !this.currentUserService.isAdmin
					: isRoutePlans &&
						(this.accountService.isClient || !this.currentUserService.isAdmin);

			if (shouldKickOut) {
				// index can redirect (agency user in a client space), which rejects a plain then.
				this.replaceRoute(defaultRedirectPath)
					.followRedirects()
					.then(() => {
						this.billingService.checkForPermissions();
					});
				return false;
			}

			switch (rootRoute) {
				case 'settings':
					redirectPath = this._validateAccountTransition(targetName);
					break;
			}
		}

		if (redirectPath) {
			this.replaceRoute(redirectPath);
			return false;
		}

		return true;
	}

	private _validateAccountTransition(targetName: string): string {
		let redirectPath = '';

		if (targetName.startsWith('settings')) {
			if (!this.currentUserService.isAdmin) {
				if (
					!targetName.includes('level') &&
					!targetName.includes('notifications') &&
					!targetName.includes('index')
				) {
					redirectPath = 'settings.notifications';
				}
			}
		} else {
			if (targetName.startsWith('settings.level')) {
				if (!this.accountProgressService.canSeeAccountProgressSubpage) {
					redirectPath = 'index';
				}
			} else if (targetName.startsWith('account.clients')) {
				if (!this.currentUserService.isAdmin || !this.accountService.isAgency) {
					redirectPath = 'index';
				}
			} else if (!this.currentUserService.isAdmin) {
				redirectPath = 'index';
			}
		}

		return redirectPath;
	}

	triggerEventForEmailCampaign(): void {
		const searchParams = new URLSearchParams(window.location.search),
			searchParamsUtmMedium = searchParams.get('utm_medium');

		if (!searchParamsUtmMedium?.includes('email')) {
			return;
		}

		const campaignEventData: APIData.Tracking.Data = {};
		const paramsMap: Record<string, string> = {
			utm_campaign: 'campaign',
			utm_source: 'source',
			utm_content: 'content',
		};

		Object.entries(paramsMap).forEach(([paramKey, eventKey]) => {
			if (searchParams.has(paramKey)) {
				campaignEventData[eventKey] = searchParams.get(paramKey);
			}
		});

		this.analyticsService.event(TrackingEvent.EMAIL_CLICKED, campaignEventData);
	}

	getNormalizedTrackingNameForPath() {
		return this.analyticsService.getNormalizedTrackingPathName(
			this.router.currentRouteName || '',
		);
	}

	createFullscreenLoaderModal(): PopupModal {
		return this.popupService.createModal(NavigationPopupFullscreenLoaderComponent, {
			popupClass: 'popup--fullscreen',
			showClose: false,
			isAutoCentered: false,
		});
	}

	createContextSwitchModal(): PopupModal {
		return this.popupService.createModal(CommonPopupContextSwitchComponent, {
			popupClass: 'popup-type-context-switch',
		});
	}

	openGoogleSheetsVideoPopup(origin: 'designer' | 'sheets_wizard' | 'onboarding'): PopupModal {
		const url = 'https://player.vimeo.com/video/842396594';
		const popup = this.openVideoPopup(url);
		this.analyticsService.event(TrackingEvent.QUERY_SHEETS_VIDEO_SEEN, { origin });
		return popup;
	}

	openIntroTourVideoPopup(
		isFirst: boolean,
		isClientAccountManager: boolean,
		origin: string,
	): PopupModal {
		let videoId;
		if (isFirst) {
			videoId = '908589785?h=d6512610b2';
		} else if (isClientAccountManager) {
			videoId = '890362758';
		} else {
			videoId = '896159650';
		}
		const url = `https://player.vimeo.com/video/${videoId}`;
		const popup = this.openVideoPopup(url);
		this.analyticsService.event(TrackingEvent.INTRO_VIDEO, { origin });
		return popup;
	}

	openVideoPopup(url: string, autoplay = false): PopupModal {
		const popup = this.popupService.createModal(CommonPopupsVideoComponent, {
			popupClass: 'videoPopup no-edges',
		});

		popup.open({ url, autoplay });

		return popup;
	}

	openImageUploadPopup(
		onFinish: PopupData.Common.ImageUpload['onFinish'],
		blobUrl = '',
		existingUrl = '',
		crop?: ImageCropData | null,
		imageAspect?: number,
	) {
		const popup = this.popupService.createModal(CommonImageUploadPopupComponent, {
			popupClass: 'popup-type-image-upload',
		});

		const popupData: PopupData.Common.ImageUpload = {
			onFinish: (url, originalUrl, crop) => {
				onFinish(url, originalUrl, crop);
			},
		};

		if (blobUrl) {
			popupData.imageBlobUrl = blobUrl;
		}
		if (existingUrl) {
			popupData.imageOriginalUrl = existingUrl;
		}
		if (crop) {
			popupData.imageCrop = crop;
		}
		if (imageAspect) {
			popupData.imageAspect = imageAspect;
		}

		popup.open(popupData);

		return popup;
	}

	openDebugPopup(blockId?: number): PopupModal | null {
		if (this.popupService.isOpen(CommonPopupsDebuggingComponent)) {
			return null;
		}

		const popup = this.popupService.createModal(CommonPopupsDebuggingComponent, {
			popupClass: 'debuggingpopup',
		});
		popup.open({ blockId });
		return popup;
	}

	openLogoutPopup() {
		const popup = this.popupService.createModal(NavigationPopupLogoutComponent, {
			popupClass: 'popup-type-logout',
			closeOnOverlayClick: false,
			showClose: false,
		});
		popup.open();
	}

	createMobileAppSuggestionPopup() {
		return this.popupService.createModal(MobilePopupAppSuggestionComponent, {
			popupClass: 'popup-type-mobile-app-suggestion',
		});
	}

	openPreviewBoardWithLimitation(modelId: string): void {
		redirect(`/databoards/view/${modelId}/limitation`);
	}

	checkUrlToOpenChat() {
		if (window.location.hash === '#chat') {
			schedule('afterRender', () => {
				this.currentUserService.chatWithSupport();
			});
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		navigation: NavigationService;
	}
}
